import React, { useState, useRef, useEffect } from 'react';
import './ImageCropper.css';
import CuadroTexto from '../static/assets/cuadroTexto/cuadroTexto';

const ImageCropper = ({ src, onCropped, onCancel }) => {
  const [imageSrc, setImageSrc] = useState(src);
  const [crop, setCrop] = useState({ x: 50, y: 50, width: 100, height: 100 });
  const containerRef = useRef(null);

  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, cropX: 0, cropY: 0 });

  const [resizing, setResizing] = useState(false);
  const resizeStart = useRef({ x: 0, y: 0, cropSize: 0 });
  const [resizeDir, setResizeDir] = useState(null);

  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    setImageSrc(src);
  }, [src]);

  const handleAreaDown = (e) => {
    e.preventDefault();
    if (e.currentTarget !== e.target) return;
    setDragging(true);
    dragStart.current = {
      x: e.clientX ?? e.touches[0].clientX,
      y: e.clientY ?? e.touches[0].clientY,
      cropX: crop.x,
      cropY: crop.y,
    };
    containerRef.current.setPointerCapture?.(e.pointerId);
  };

  const handleResizeDown = (e, direction) => {
    e.preventDefault();
    setResizing(true);
    setResizeDir(direction);
    resizeStart.current = {
      x: e.clientX ?? e.touches[0].clientX,
      y: e.clientY ?? e.touches[0].clientY,
      cropSize: crop.width,
      cropX: crop.x,
      cropY: crop.y,
    };
    containerRef.current.setPointerCapture?.(e.pointerId);
  };

  const handleMove = (e) => {
    e.preventDefault();
    const cx = e.clientX ?? e.touches?.[0].clientX;
    const cy = e.clientY ?? e.touches?.[0].clientY;
    const container = containerRef.current;
    if (!container) return;

    if (dragging) {
      const dx = cx - dragStart.current.x;
      const dy = cy - dragStart.current.y;
      setCrop((prev) => {
        const newX = Math.max(0, dragStart.current.cropX + dx);
        const newY = Math.max(0, dragStart.current.cropY + dy);
        return {
          ...prev,
          x: Math.min(newX, container.clientWidth - prev.width),
          y: Math.min(newY, container.clientHeight - prev.height),
        };
      });
    } else if (resizing && resizeDir) {
      const rawDx = cx - resizeStart.current.x;
      const rawDy = cy - resizeStart.current.y;
      const signX = resizeDir.includes('l') ? -1 : 1;
      const signY = resizeDir.includes('t') ? -1 : 1;
      const delta = Math.min(signX * rawDx, signY * rawDy);
      setCrop((prev) => {
        let newSize = Math.max(20, resizeStart.current.cropSize + delta);
        const maxRight = container.clientWidth - resizeStart.current.cropX;
        const maxBottom = container.clientHeight - resizeStart.current.cropY;
        const maxLeft =
          resizeStart.current.cropX + resizeStart.current.cropSize;
        const maxTop = resizeStart.current.cropY + resizeStart.current.cropSize;
        const maxSizeX = resizeDir.includes('l') ? maxLeft : maxRight;
        const maxSizeY = resizeDir.includes('t') ? maxTop : maxBottom;
        newSize = Math.min(newSize, maxSizeX, maxSizeY);

        let newX = prev.x;
        let newY = prev.y;
        if (resizeDir.includes('l')) {
          newX =
            resizeStart.current.cropX +
            (resizeStart.current.cropSize - newSize);
        }
        if (resizeDir.includes('t')) {
          newY =
            resizeStart.current.cropY +
            (resizeStart.current.cropSize - newSize);
        }
        return { x: newX, y: newY, width: newSize, height: newSize };
      });
    }
  };

  const handleUp = (e) => {
    e.preventDefault();
    setDragging(false);
    setResizing(false);
    setResizeDir(null);
    containerRef.current.releasePointerCapture?.(e.pointerId);
  };

  const cropImage = () => {
    if (!imageSrc) return;
    const img = new Image();
    img.src = imageSrc;
    img.onload = () => {
      const { clientWidth: cw, clientHeight: ch } = containerRef.current;
      const aspectImg = img.naturalWidth / img.naturalHeight;
      const aspectCont = cw / ch;
      let renderedW,
        renderedH,
        offsetX = 0,
        offsetY = 0;

      if (aspectImg > aspectCont) {
        renderedW = cw;
        renderedH = cw / aspectImg;
        offsetY = (ch - renderedH) / 2;
      } else {
        renderedH = ch;
        renderedW = ch * aspectImg;
        offsetX = (cw - renderedW) / 2;
      }

      const scaleX = img.naturalWidth / renderedW;
      const scaleY = img.naturalHeight / renderedH;
      const sx = crop.x - offsetX;
      const sy = crop.y - offsetY;

      const canvas = document.createElement('canvas');
      canvas.width = crop.width * scaleX;
      canvas.height = crop.height * scaleY;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(
        img,
        sx * scaleX,
        sy * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        crop.width * scaleX,
        crop.height * scaleY
      );
      canvas.toBlob((blob) => {
        if (!blob) return;
        onCropped(new File([blob], 'photo.jpg', { type: blob.type }));
      }, 'image/jpeg');
    };
  };

  return (
    <div>
      {errorMessage && (
        <CuadroTexto
          message={errorMessage}
          onAccept={() => setErrorMessage('')}
          showCancel={false}
        />
      )}

      {imageSrc && (
        <div>
          <div
            ref={containerRef}
            data-testid="crop-container"
            className="crop-container"
            onPointerMove={handleMove}
            onPointerUp={handleUp}
            onPointerLeave={handleUp}
            onTouchMove={handleMove}
            onTouchEnd={handleUp}
          >
            <img src={imageSrc} alt="A recortar" className="crop-image" />
            <div
              className="crop-area"
              data-testid="crop-area"
              style={{
                left: crop.x,
                top: crop.y,
                width: crop.width,
                height: crop.height,
              }}
              onPointerDown={handleAreaDown}
              onTouchStart={handleAreaDown}
            >
              {['tl', 'tr', 'bl', 'br'].map((dir) => (
                <div
                  key={dir}
                  className={`resize-handle ${dir}`}
                  data-testid="resize-handle"
                  onPointerDown={(e) => handleResizeDown(e, dir)}
                  onTouchStart={(e) => handleResizeDown(e, dir)}
                />
              ))}
            </div>
          </div>

          <div className="crop-controls">
            <button className="crop-cancel-btn" onClick={onCancel}>
              Cancelar
            </button>
            <button className="crop-save-btn" onClick={cropImage}>
              Recortar y Guardar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageCropper;
